import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import { Button } from '@/components/ui/Button';
import { RecentFixture } from '@/types/fixtures';
import { VesselBadge } from './VesselBadge';
import { FixtureVesselSizeBadge } from './FixtureVesselSizeBadge';
import { FixtureStatus } from './FixtureStatus';
import { formatCargoSize, formatLayCan, formatRate } from '@/utils/fixtureUtils';

interface RecentFixtureItemProps {
  fixture: RecentFixture;
  isSelected?: boolean;
  hideMoreDetails?: boolean; // For dashboard/preview mode - disables expansion
  onToggleSelected?: (fixtureId: number) => void; // Parent control over expansion
  /** Strip outer card chrome when nested in a parent list (e.g. Daily Report) */
  embedded?: boolean;
}

/**
 * Compact fixture item matching Angular implementation
 * Uses a grid layout with vessel, trade, and earning sections
 */
export function RecentFixtureItem({
  fixture,
  isSelected = false,
  hideMoreDetails = false,
  onToggleSelected,
  embedded = false,
}: RecentFixtureItemProps) {
  const [expanded, setExpanded] = useState(isSelected);
  const navigate = useNavigate();

  const handleToggle = () => {
    if (hideMoreDetails) {
      // In dashboard/preview mode, navigate directly to details
      navigate(`/recent-fixtures/${fixture.fixtureId}`);
    } else if (onToggleSelected) {
      // Parent controls expansion
      onToggleSelected(fixture.fixtureId);
    } else {
      // Self-controlled expansion
      setExpanded(!expanded);
    }
  };

  const handleViewDetails = () => {
    navigate(`/recent-fixtures/${fixture.fixtureId}`);
  };

  // Format laycan dates using Angular's logic
  const layCan = formatLayCan(fixture.layCanStart, fixture.layCanEnd);

  // Format rate details using Angular's logic
  const rateDetails = fixture.rate ? formatRate(fixture.rate, fixture.rateType) : null;

  // Format actual TCE earning
  const actualTceEarning = fixture.tcactInclIdleEarning
    ? `${Math.round(fixture.tcactInclIdleEarning / 1000)}K/Day`
    : 'No Data';

  return (
    <div
      className={clsx(
        'overflow-hidden transition-all duration-200',
        embedded
          ? 'rounded-none border-0'
          : 'rounded-lg border border-gray-200 dark:border-dark-700',
        !embedded && expanded && 'mb-4 mt-2 shadow-lg ring-1 ring-gray-200 dark:ring-gray-700'
      )}
      style={{ containerType: 'inline-size' }}
      id={`fixture-${fixture.fixtureId}`}
    >
      {/* Main Row - Clickable */}
      <div
        className={clsx(
          'fixture-grid cursor-pointer bg-white px-2 py-1.5 pr-3 transition-colors hover:bg-gray-50 dark:bg-dark-800 dark:hover:bg-dark-700',
          !hideMoreDetails && 'fixture-grid-chevron'
        )}
        onClick={handleToggle}
      >
        {/* Vessel Section: vessel info + operator (right aligned) */}
        <div className="fixture-vessel flex items-center">
          {/* Caret + Vessel Info */}
          <div className="mr-2 flex items-center">
            {!hideMoreDetails && (
              <ChevronRight
                className={`mr-1 h-5 w-5 flex-shrink-0 text-gray-400 transition-transform duration-200 ${
                  expanded ? 'rotate-90' : ''
                }`}
              />
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
                  {fixture.vesselName}
                </span>
                <VesselBadge
                  isOwnFixture={fixture.isOwnFixture}
                  scrubberInstalled={fixture.scrubberInstalled}
                  age15Plus={fixture.age >= 15}
                />
              </div>
              {fixture.cargoSize > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {fixture.type} | {formatCargoSize(fixture.cargoSize)}
                </div>
              )}
            </div>
          </div>
          {/* Operator - right aligned (vessel size badge sits above the operator) */}
          <div className="flex-1 text-right">
            <div className="flex flex-col items-end gap-0.5">
              <FixtureVesselSizeBadge vesselSizeId={fixture.vesselSizeId} />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 sm:text-sm">
                {fixture.operator}
              </span>
            </div>
            {fixture.rateExplanation && (
              <div className="mt-0.5 text-xs text-red-600 dark:text-red-400">
                {fixture.rateExplanation}
              </div>
            )}
          </div>
        </div>

        {/* Trade Section: trade/laycan + charterer (right aligned) */}
        <div className="fixture-trade flex items-center">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
              {fixture.trade}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {layCan}
              {rateDetails && <> | {rateDetails}</>}
            </div>
          </div>
          {/* Charterer - right aligned */}
          <div className="text-right text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
            {fixture.charterer}
          </div>
        </div>

        {/* Earning Section: status + earning */}
        <div className="fixture-earning flex items-center text-xs sm:text-sm">
          <FixtureStatus status={fixture.status} className="text-sm sm:text-base" />
          <span
            className={`flex-1 whitespace-nowrap text-right font-medium ${
              fixture.tcactInclIdleEarning
                ? 'text-gray-900 dark:text-white'
                : 'text-gray-400 dark:text-gray-500'
            }`}
          >
            {actualTceEarning}
          </span>
        </div>
      </div>

      <style>{`
        .fixture-grid {
          display: grid;
          grid-template-columns: 1fr;
        }

        .fixture-vessel,
        .fixture-trade,
        .fixture-earning {
          padding: 0.25rem;
        }

        @container (min-width: 960px) {
          .fixture-grid {
            grid-template-columns: repeat(5, 1fr);
            align-items: center;
          }

          .fixture-vessel {
            grid-column: 1 / 3;
          }

          .fixture-trade {
            grid-column: 3 / 5;
          }

          .fixture-earning {
            grid-column: 5 / 6;
          }
        }

        @media (max-width: 576px) {
          .fixture-vessel,
          .fixture-trade,
          .fixture-earning {
            padding: 0.25rem 0;
          }
        }

        .fixture-details-panel {
          padding-left: 0.75rem;
          padding-right: 0.75rem;
        }

        @container (max-width: 959.98px) {
          .fixture-grid-chevron .fixture-trade,
          .fixture-grid-chevron .fixture-earning {
            padding-left: 1.5rem;
          }

          .fixture-details-panel {
            padding-left: 2rem;
            padding-right: 0.75rem;
          }
        }
      `}</style>

      {/* Expanded Details - Matching Angular Layout */}
      {expanded && !hideMoreDetails && (
        <div className="fixture-details-panel border-t border-gray-200 bg-gray-50 py-3 dark:border-dark-700 dark:bg-gray-900/50">
          <h4 className="mb-3 text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Fixture Details
          </h4>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {/* Coming From */}
            <DetailItem label="COMING FROM" value={fixture.comingFrom || '--'} />

            {/* Ballast Cons/Speed */}
            <DetailItem
              label="BALLAST CONS/SPEED"
              value={
                fixture.ballastConsumption && fixture.ballastSpeed
                  ? `${fixture.ballastConsumption.toFixed(1)} mts @ ${fixture.ballastSpeed.toFixed(1)} knts`
                  : '--'
              }
            />

            {/* Laden Cons/Speed */}
            <DetailItem
              label="LADEN CONS/SPEED"
              value={
                fixture.ladenConsumption && fixture.ladenSpeed
                  ? `${fixture.ladenConsumption.toFixed(1)} mts @ ${fixture.ladenSpeed.toFixed(1)} knts`
                  : '--'
              }
            />

            {/* Idle Days */}
            <DetailItem label="IDLE DAYS" value={fixture.idleDays?.toFixed(1) || '0.0'} />

            {/* RV TCE Exclude Idle */}
            <DetailItem
              label="RV TCE EXCLUDE IDLE"
              value={
                fixture.tcrtExclIdleEarning
                  ? `${Math.round(fixture.tcrtExclIdleEarning / 1000)}K/Day @ ${fixture.tcrtInclIdleDays?.toFixed(1) || '0.0'} days`
                  : '--'
              }
            />

            {/* Actual TCE Include Idle */}
            <DetailItem
              label="ACTUAL TCE INCLUDE IDLE"
              value={
                fixture.tcactInclIdleEarning
                  ? `${Math.round(fixture.tcactInclIdleEarning / 1000)}K/Day @ ${fixture.tcactInclIdleDays?.toFixed(1) || '0.0'} days`
                  : '--'
              }
            />
          </div>

          <div className="mt-4 text-center">
            <Button
              variant="primary"
              size="sm"
              onClick={handleViewDetails}
              rightIcon={<ChevronRight className="h-4 w-4" />}
            >
              View Full Details
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface DetailItemProps {
  label: string;
  value: string | null | undefined;
}

function DetailItem({ label, value }: DetailItemProps) {
  return (
    <div className="border-b border-gray-200 pb-2 dark:border-gray-700 md:border-b-0 md:pb-0">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="text-sm font-medium text-gray-900 dark:text-white">{value || '--'}</div>
    </div>
  );
}
